// src/components/SmartFlow/nodeConfigRegistry.js

/**
 * Node Config Registry
 *
 * Maps node types to their dedicated configuration components.
 * This allows WorkflowBuilder and NodeConfigModal to remain generic
 * while supporting 100+ different node types.
 *
 * How to add a new node type:
 *
 * 1. Create the config component in Nodes/<NodeType>/<NodeType>Config.jsx
 * 2. Import it here
 * 3. Add to NODE_CONFIG_COMPONENTS map
 *
 * Example:
 * ```
 * import SendEmailConfig from './Nodes/SendEmail/SendEmailConfig';
 *
 * const NODE_CONFIG_COMPONENTS = {
 *   'HTTP_REQUEST': HttpRequestConfig,
 *   'SEND_EMAIL': SendEmailConfig,  // New node
 * };
 * ```
 *
 * Config Component Interface:
 *
 * All config components must accept these props:
 *
 * @prop {Object} config - Current node configuration
 * @prop {Object} nodeMetadata - Node metadata from backend (schema, description, etc.)
 * @prop {Function} onChange - Callback to update config: (newConfig) => void
 * @prop {Object} validationErrors - Validation errors from backend: { fieldName: "error message" }
 * @prop {Object} inputData - (Optional) Data from previous node for preview/expressions
 *
 * Example component:
 * ```jsx
 * const MyNodeConfig = ({ config, nodeMetadata, onChange, validationErrors }) => {
 *   const handleFieldChange = (fieldName, value) => {
 *     onChange({ ...config, [fieldName]: value });
 *   };
 *
 *   return (
 *     <div>
 *       <input
 *         value={config.myField || ''}
 *         onChange={(e) => handleFieldChange('myField', e.target.value)}
 *       />
 *       {validationErrors.myField && <div>{validationErrors.myField}</div>}
 *     </div>
 *   );
 * };
 * ```
 */


import HttpRequestConfig from './nodes/node/HTTP_Request/HttpRequestConfig';
import ConditionConfig from './nodes/node/Condition/ConditionConfig';
import SendMessageEvolutionConfig from './nodes/node/SendMessageEvolution/SendMessageEvolutionConfig'

/**
 * Registry of node configuration components
 *
 * Key: node_type (from backend NodeRegistry)
 * Value: React component for configuration
 */
const NODE_CONFIG_COMPONENTS = {
  'HTTP_REQUEST': HttpRequestConfig,
  'CONDITION': ConditionConfig,
  'EVOLUTION_SEND': SendMessageEvolutionConfig,
  // Add more node types here as they are implemented:
  // 'WEBHOOK': WebhookConfig,
  // 'SET_VARIABLE': SetVariableConfig,
  // 'IF_CONDITION': IfConditionConfig,
  // 'DELAY': DelayConfig,
  // 'SEND_EMAIL': SendEmailConfig,
  // 'DATABASE_QUERY': DatabaseQueryConfig,
  // 'TRANSFORM_DATA': TransformDataConfig,
  // etc.
};

/**
 * Get configuration component for a node type
 *
 * @param {string} nodeType - Node type identifier (e.g., 'HTTP_REQUEST')
 * @returns {React.Component|null} - Configuration component or null if not found
 */
export const getNodeConfigComponent = (nodeType) => {
  return NODE_CONFIG_COMPONENTS[nodeType] || null;
};

/**
 * Check if a node type has a dedicated configuration component
 *
 * @param {string} nodeType - Node type identifier
 * @returns {boolean} - True if custom component exists
 */
export const hasCustomConfigComponent = (nodeType) => {
  return nodeType in NODE_CONFIG_COMPONENTS;
};

/**
 * Get all registered node types
 *
 * @returns {string[]} - Array of registered node type identifiers
 */
export const getRegisteredNodeTypes = () => {
  return Object.keys(NODE_CONFIG_COMPONENTS);
};

export default {
  getNodeConfigComponent,
  hasCustomConfigComponent,
  getRegisteredNodeTypes,
};
